import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";

interface UserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  cpf?: string;
  full_name?: string;
  role: "patient" | "psychologist" | "admin";
  avatar_url?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  appointment_reminders?: boolean;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signUp: (email: string, password: string, fullName: string, role: "patient" | "psychologist") => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; role?: string }>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<boolean>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state with improved error handling
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('🔔 Auth state changed:', event, session?.user?.id || 'No user');
            
            if (!mounted) return;

            // Handle different auth events
            switch (event) {
              case 'SIGNED_IN':
                console.log('✅ User signed in:', session?.user?.email);
                setSession(session);
                setUser(session?.user ?? null);
                
                // Defer profile fetch to avoid blocking auth state update
                if (session?.user) {
                  setTimeout(() => {
                    if (mounted) {
                      fetchUserProfile(session.user.id, session.user.email);
                    }
                  }, 0);
                }
                break;

              case 'SIGNED_OUT':
                console.log('👋 User signed out');
                setSession(null);
                setUser(null);
                setProfile(null);
                // Clear any stored tokens
                localStorage.removeItem('sb-olpuhxpkqkwojdoricuo-auth-token');
                break;

              case 'TOKEN_REFRESHED':
                console.log('🔄 Token refreshed successfully');
                setSession(session);
                setUser(session?.user ?? null);
                break;

              case 'USER_UPDATED':
                console.log('👤 User updated');
                setSession(session);
                setUser(session?.user ?? null);
                break;

              default:
                setSession(session);
                setUser(session?.user ?? null);
                if (!session) {
                  setProfile(null);
                }
            }
            
            setIsLoading(false);
          }
        );

        // THEN check for existing session with retry logic
        const checkSession = async () => {
          try {
            const { data: { session: currentSession }, error } = await supabase.auth.getSession();
            
            if (error) {
              console.error('❌ Session check error:', error);
              
              // If it's a refresh token error, try to recover
              if (error.message.includes('refresh') && retryCount < maxRetries) {
                retryCount++;
                console.log(`🔄 Retrying session check (${retryCount}/${maxRetries})`);
                setTimeout(checkSession, 1000 * retryCount);
                return;
              }
              
              // Clear invalid session
              setSession(null);
              setUser(null);
              setProfile(null);
              setIsLoading(false);
              return;
            }

            console.log('🔍 Initial session check:', currentSession?.user?.id || 'No session');

            if (mounted) {
              setSession(currentSession);
              setUser(currentSession?.user ?? null);
              
              if (currentSession?.user) {
                await fetchUserProfile(currentSession.user.id, currentSession.user.email);
              }
              
              setIsLoading(false);
            }
          } catch (error) {
            console.error('❌ Unexpected session error:', error);
            if (mounted) {
              setSession(null);
              setUser(null);
              setProfile(null);
              setIsLoading(false);
            }
          }
        };

        await checkSession();

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const fetchUserProfile = async (userId: string, userEmail?: string) => {
    try {
      console.log('👤 Fetching profile for user:', userId, 'email:', userEmail);
      
      let userRole: "patient" | "psychologist" | "admin" = 'patient';
      
      // Admin check
      if (userEmail === 'psy@psyonline.clinic') {
        userRole = 'admin';
        console.log('🔐 User identified as admin by email');
      } else {
        // Check if user is a psychologist
        const { data: psychologistData, error: psychologistError } = await supabase
          .from('psychologists')
          .select('id, is_approved, status, user_id')
          .eq('user_id', userId)
          .single();

        if (psychologistData && !psychologistError) {
          userRole = 'psychologist';
          console.log('👨‍⚕️ User identified as psychologist');
        }
      }

      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Error fetching profile:', profileError);
      }

      const profileWithRole: UserProfile = {
        id: userId,
        email: userEmail || profileData?.email || '',
        first_name: profileData?.first_name || '',
        last_name: profileData?.last_name || '',
        phone: profileData?.phone || '',
        cpf: profileData?.cpf || '',
        full_name: profileData?.first_name && profileData?.last_name 
          ? `${profileData.first_name} ${profileData.last_name}` 
          : profileData?.first_name || '',
        role: userRole,
        avatar_url: profileData?.avatar_url || '',
        email_notifications: profileData?.email_notifications ?? true,
        sms_notifications: profileData?.sms_notifications ?? false,
        appointment_reminders: profileData?.appointment_reminders ?? true,
        created_at: profileData?.created_at || new Date().toISOString()
      };

      console.log('✅ Profile loaded with role:', profileWithRole.role);
      setProfile(profileWithRole);
      return profileWithRole;
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      return null;
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
      console.log('🔄 Refreshing session...');
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Session refresh failed:', error);
        return false;
      }

      if (session) {
        console.log('✅ Session refreshed successfully');
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Unexpected refresh error:', error);
      return false;
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    fullName: string, 
    role: "patient" | "psychologist"
  ): Promise<boolean> => {
    setIsLoading(true);
    console.log('📝 Sign up attempt:', { email, role });
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      console.log('📝 Sign up response:', { user: data.user?.id, error });

      if (error) {
        console.error('❌ Sign up error:', error);
        toast.error(error.message || 'Erro ao criar conta');
        return false;
      }

      if (data.user) {
        toast.success('Conta criada com sucesso! Verifique seu email.');
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Sign up error:', error);
      toast.error('Erro inesperado ao criar conta');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; role?: string }> => {
    setIsLoading(true);
    console.log('🔑 Sign in attempt:', { email });
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      console.log('🔑 Sign in response:', { 
        user: data.user?.id, 
        session: !!data.session,
        error: error?.message 
      });

      if (error) {
        console.error('❌ Sign in error:', error);
        
        // Provide more specific error messages
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Credenciais inválidas. Verifique seu email e senha.');
        } else if (error.message.includes('Email not confirmed')) {
          toast.error('Email não confirmado. Verifique sua caixa de entrada.');
        } else {
          toast.error(error.message || 'Erro ao fazer login');
        }
        return { success: false };
      }

      if (data.user && data.session) {
        console.log('✅ Login successful for user:', data.user.id);
        
        const profile = await fetchUserProfile(data.user.id, data.user.email);
        
        if (profile) {
          console.log('🎯 Role determined for redirect:', profile.role);
          toast.success('Login realizado com sucesso!');
          return { success: true, role: profile.role };
        }
        
        return { success: true, role: 'patient' };
      }

      console.warn('⚠️ Login returned no user/session');
      return { success: false };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      toast.error('Erro inesperado ao fazer login');
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      console.log('👋 Sign out initiated');
      
      // Clear local state immediately to prevent UI confusion
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Only attempt Supabase logout if we have a valid session
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession) {
        console.log('🔑 Valid session found, proceeding with Supabase logout');
        const { error } = await supabase.auth.signOut();
        
        if (error) {
          console.error('❌ Supabase logout error:', error);
          // Don't show error toast for session-related issues
          if (!error.message.includes('session') && !error.message.includes('Session')) {
            toast.error('Erro ao fazer logout');
          }
        } else {
          console.log('✅ Supabase logout successful');
        }
      } else {
        console.log('ℹ️ No active session found, clearing local state only');
      }
      
      // Always clear localStorage regardless of Supabase logout result
      try {
        localStorage.removeItem('sb-olpuhxpkqkwojdoricuo-auth-token');
        localStorage.clear(); // Clear all auth-related data
      } catch (storageError) {
        console.warn('⚠️ Error clearing localStorage:', storageError);
      }
      
      console.log('✅ Logout completed successfully');
      toast.success('Logout realizado com sucesso!');
      
    } catch (error) {
      console.error('❌ Unexpected logout error:', error);
      
      // Still clear local state even if logout fails
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Clear localStorage
      try {
        localStorage.removeItem('sb-olpuhxpkqkwojdoricuo-auth-token');
        localStorage.clear();
      } catch (storageError) {
        console.warn('⚠️ Error clearing localStorage:', storageError);
      }
      
      // Show success message even if there was an error, since we cleared local state
      toast.success('Logout realizado com sucesso!');
    }
  };

  const logout = signOut;

  const updateProfile = async (updates: Partial<UserProfile>): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        console.error('❌ Profile update error:', error);
        toast.error('Erro ao atualizar perfil');
        return false;
      }

      await fetchUserProfile(user.id, user.email);
      toast.success('Perfil atualizado com sucesso!');
      return true;
    } catch (error) {
      console.error('❌ Profile update error:', error);
      toast.error('Erro inesperado ao atualizar perfil');
      return false;
    }
  };

  const contextValue = {
    user,
    profile,
    session,
    isLoading,
    isAuthenticated: !!user && !!session,
    signUp,
    signIn,
    signOut,
    logout,
    updateProfile,
    refreshSession
  };

  console.log('🔍 AuthContext render:', { 
    isAuthenticated: contextValue.isAuthenticated, 
    isLoading, 
    profileRole: profile?.role,
    userEmail: user?.email 
  });

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
